import * as types from '@/store/types/elements'
import {
  setActiveElement,
  setPages,
  toggleContextMenu,
  removeElement,
  closeAlert,
  addElement,
  setForceUpdate,
  setElements,
  toggleToaster
} from '@/store/actions'
import WebWorker from '@/workers/elementsSave.worker'
import { PagesResource } from '@/resources'

const elementWorker = new WebWorker()

/**
 * Swap elements index
 * @param {Object} firstEl
 * @param {Object} secondEl
 */
const swapPosition = (firstEl, secondEl, action) => {
  const tempPos = secondEl.position

  if (
    (action === 'send_to_back' || action === 'send_to_front') &&
    firstEl.position !== secondEl.position
  ) {
    secondEl.content.styles = {
      ...secondEl.content.styles,
      zIndex: firstEl.position
    }
    return (secondEl.position = firstEl.position)
  }

  secondEl.content.styles = {
    ...secondEl.content.styles,
    zIndex: firstEl.position
  }

  firstEl.content.styles = {
    ...firstEl.content.styles,
    zIndex: tempPos
  }

  /* VERIFICA SE OS ELEMENTOS POSSUEM A MESMA POSIÇÃO E ALTERA A POSIÇÃO */
  if (firstEl.position === secondEl.position) {
    if (action === 'send_to_back_one' || action === 'send_to_back')
      return (firstEl.position = +tempPos + 1)
    if (action === 'send_to_front_one' || action === 'send_to_front')
      return (firstEl.position = +tempPos - 1)
  }

  secondEl.position = firstEl.position
  firstEl.position = tempPos
}

/**
 * Save an element on database
 * @param {Integer} id
 * @param {Integer} id_page
 * @param {Object} content
 * @param {Token} accessToken
 */
const save = (id, id_page, content, accessToken, callback = null) => {
  elementWorker.postMessage({
    id: id,
    pageID: id_page,
    item: {
      content: JSON.stringify(content)
    },
    accessToken: accessToken
  })

  // WHEN SAVE IS COMPOLETE
  elementWorker.onmessage = () => {
    return callback && callback()
  }
}

/**
 * Save several elements on database
 * @param {Object} payload
 */
const batchSave = payload => {
  const { id_page, accessToken, elements } = payload

  elements.map((el, index) => {
    elementWorker.postMessage({
      id: el.id,
      pageID: id_page,
      item: {
        position: el.position || index,
        content: JSON.stringify(el.content)
      },
      accessToken: accessToken
    })
    return el
  })
}

const updatePagesElements = (pages, page_id, newElements, next) => {
  const newPages = pages.map(item => {
    if (+item.id === +page_id) {
      item.items = newElements
    }
    return item
  })
  next(setPages(newPages))
}

/**
 * When add a new element find the relative page
 * and update de page items
 * @param {Store} store
 */
const onAddElementMiddleware = store => dispatch => action => {
  if (action.type !== types.ADD_ELEMENT) {
    return dispatch(action)
  }
  const { id_page } = action.payload
  const pages = store.getState().pages.map(page => {
    if (+page.id === +id_page) {
      page.items.push(action.payload)
    }
    return page
  })
  dispatch(setPages(pages))
  return dispatch(action)
}

/**
 * When updates an item save on database
 * @param {Store} store
 */
const onUpdateElementMiddleware = store => dispatcher => action => {
  if (action.type === types.UPDATE_ELEMENT) {
    const { id, pageID, accessToken, element } = action.payload
    store.getState().pages.map(p => {
      if (+p.id === +pageID) {
        const index = p.items.findIndex(i => +i.id === +id)
        p.items[index] = element
      }
      return p
    })

    const { keys } = store.getState().languages

    save(id, pageID, element.content, accessToken, () => {
      dispatcher(
        toggleToaster({
          isVisible: true,
          message: keys['general-saving'],
          position: 'top-right',
          type: 'success'
        })
      )
    })
  }

  if (action.type === types.BATCH_UPDATE) {
    dispatcher(setForceUpdate())
    batchSave(action.payload)
  }

  return dispatcher(action)
}

/**
 * ON DELETE AN ELEMENT
 * @param {STORE} store
 */
const onDeleteElementMiddleware = store => dispatcher => action => {
  if (action.type === types.DELETE_ELEMENT) {
    const { id_page, id } = action.payload
    const { pages, elements } = store.getState()
    const newElements = elements.filter(item => +item.id !== +id)

    dispatcher(toggleContextMenu({ show: false }))
    dispatcher(setActiveElement(null))
    dispatcher(removeElement(action.payload.id))
    dispatcher(closeAlert())
    updatePagesElements(pages, id_page, newElements, dispatcher)
  }

  return dispatcher(action)
}

/**
 * When duplicates a item
 * @param {Store} store
 */
const onDuplicateElementMiddleware = store => dispatcher => action => {
  if (action.type !== types.DUPLICATE_ELEMENT) {
    return dispatcher(action)
  }
  const { payload } = action
  const content = { ...payload.content }
  const { attributes } = content
  const newTop = +attributes.top.replace(/px/, '') + 30
  const newLeft = +attributes.left.replace(/px/, '') + 30

  content.attributes = {
    ...attributes,
    top: `${newTop}px`,
    left: `${newLeft}px`
  }

  dispatcher(setActiveElement(null))
  dispatcher(toggleContextMenu({ show: false }))
  PagesResource.savePageItem(payload.id_page, {
    content: JSON.stringify({ ...content }),
    position: +payload.position + 1
  })
    .then(({ data }) => {
      return dispatcher(addElement(data))
    })
    .then(data => {
      const pages = store.getState().pages.map(page => {
        if (+page.id === +payload.id_page) {
          page.items.push(data.payload)
        }
        return page
      })
      dispatcher(setPages(pages))
    })
}

/**
 * when send element to back
 * @param {Store} store
 */
const onSendToBackOrFrontMiddleware = store => next => action => {
  if (
    action.type !== types.SEND_TO_BACK &&
    action.type !== types.SEND_TO_FRONT
  ) {
    return next(action)
  }

  if (!action.payload) {
    return next(action)
  }

  const toBack = action.type === types.SEND_TO_BACK
  const { payload } = action
  const { elements, user, pages } = store.getState()
  const newElements = elements.filter(el => +el.id !== +payload.id)
  const toSwapEl = toBack ? newElements.shift() : newElements.pop()

  // swap the position and zindex properties
  swapPosition(toSwapEl, payload, action.type)

  if (toBack) {
    newElements.unshift(payload, toSwapEl)
  } else {
    newElements.push(toSwapEl, payload)
  }

  /**
   * SAVE ALL ELEMENTS
   */
  batchSave({
    id_page: toSwapEl.id_page,
    elements: [toSwapEl, payload],
    accessToken: user.accessToken
  })

  // update the page elements
  updatePagesElements(pages, toSwapEl.id_page, newElements, next)

  next(setElements(newElements))
  next(setForceUpdate())
  next(toggleContextMenu({ show: false }))
  return next(action)
}

/**
 * Wehn send element to back or fron one level
 * @param {Store} store
 */
const sendToBackOrFronOneLevelMiddleware = store => next => action => {
  if (
    action.type !== types.SEND_TO_BACK_ONE &&
    action.type !== types.SEND_TO_FRONT_ONE
  ) {
    return next(action)
  }
  const toBack = action.type === types.SEND_TO_BACK_ONE
  const { payload } = action
  const { elements, user, pages } = store.getState()
  const elementIndex = elements.findIndex(el => +el.id === +payload.id)
  const toSwapIndex = toBack ? elementIndex - 1 : elementIndex + 1
  const toSwapEl = elements[toSwapIndex]

  swapPosition(toSwapEl, payload, action.type)

  elements[elementIndex] = toSwapEl
  elements[toSwapIndex] = payload

  /**
   * SAVE ALL ELEMENTS
   */
  batchSave({
    id_page: toSwapEl.id_page,
    elements: [toSwapEl, payload],
    accessToken: user.accessToken
  })

  // update the page elements
  updatePagesElements(pages, toSwapEl.id_page, elements, next)

  next(setElements(elements))
  next(setForceUpdate())
  next(toggleContextMenu({ show: false }))
  return next(action)
}

export default [
  onAddElementMiddleware,
  onUpdateElementMiddleware,
  onDeleteElementMiddleware,
  onDuplicateElementMiddleware,
  onSendToBackOrFrontMiddleware,
  sendToBackOrFronOneLevelMiddleware
]



// WEBPACK FOOTER //
// ./src/store/mniddlewares/elements.middleware.js